import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSeo } from './useSeo';
import { LANGS, LANDING_PAGES, LEGAL_PAGES, SITE_URL, homePath, landingPath, casePath, legalPath } from './seoConfig';
import { organizationSchema, personSchema, websiteSchema, webPageSchema, breadcrumbSchema, softwareAppSchema, buildGraph } from './schema';

const homeAlternates = Object.fromEntries(LANGS.map((l) => [l, homePath(l)]));

export const HomeSeo = ({ lang }) => {
  const { t } = useTranslation();
  const title = t('seo.homeTitle');
  const description = t('seo.homeDescription');
  const path = homePath(lang);
  const jsonLd = useMemo(
    () => buildGraph(organizationSchema(lang, t), personSchema(), websiteSchema(lang), webPageSchema(lang, path, title, description)),
    [lang, t, path, title, description]
  );
  useSeo({ lang, path, title, description, alternates: homeAlternates, jsonLd });
  return null;
};

export const LandingSeo = ({ lang, pageKey = 'haegendorf' }) => {
  const { t } = useTranslation();
  const page = LANDING_PAGES[pageKey];
  const title = t(page.titleKey);
  const description = t(page.descriptionKey);
  const path = landingPath(lang, pageKey);
  const alternates = useMemo(
    () => ({ ...Object.fromEntries(LANGS.map((l) => [l, landingPath(l, pageKey)])), xDefault: landingPath('de', pageKey) }),
    [pageKey]
  );
  const jsonLd = useMemo(
    () => buildGraph(
      organizationSchema(lang, t), websiteSchema(lang), webPageSchema(lang, path, title, description),
      breadcrumbSchema(lang, t, [{ name: t(`${page.i18nKey}.h1`), url: `${SITE_URL}${path}` }])
    ),
    [lang, t, path, title, description, page.i18nKey]
  );
  useSeo({ lang, path, title, description, alternates, jsonLd });
  return null;
};

export const CaseStudySeo = ({ lang, study, project }) => {
  const { t } = useTranslation();
  const title = t(`caseStudies.${study.i18nKey}.seoTitle`);
  const description = t(`caseStudies.${study.i18nKey}.seoDescription`);
  const path = casePath(lang, study.slug);
  const alternates = useMemo(
    () => ({ ...Object.fromEntries(LANGS.map((l) => [l, casePath(l, study.slug)])), xDefault: casePath('de', study.slug) }),
    [study.slug]
  );
  const jsonLd = useMemo(
    () => buildGraph(
      organizationSchema(lang, t), websiteSchema(lang), webPageSchema(lang, path, title, description),
      softwareAppSchema(project, `${SITE_URL}${path}`),
      breadcrumbSchema(lang, t, [
        { name: t('projects.title'), url: `${SITE_URL}${homePath(lang)}#projects` },
        { name: project.title, url: `${SITE_URL}${path}` },
      ])
    ),
    [lang, t, path, title, description, project]
  );
  useSeo({ lang, path, title, description, alternates, jsonLd });
  return null;
};

export const LegalSeo = ({ lang, pageKey }) => {
  const { t } = useTranslation();
  const page = LEGAL_PAGES[pageKey];
  const title = t(page.titleKey);
  const description = t(page.descriptionKey);
  const path = legalPath(lang, pageKey);
  const alternates = useMemo(
    () => ({ ...Object.fromEntries(LANGS.map((l) => [l, legalPath(l, pageKey)])), xDefault: legalPath('de', pageKey) }),
    [pageKey]
  );
  const jsonLd = useMemo(
    () => buildGraph(organizationSchema(lang, t), websiteSchema(lang), webPageSchema(lang, path, title, description)),
    [lang, t, path, title, description]
  );
  useSeo({ lang, path, title, description, alternates, jsonLd });
  return null;
};

export const NotFoundSeo = () => {
  const { t, i18n } = useTranslation();
  useSeo({ lang: (i18n.language || 'de').slice(0, 2), path: '/404', title: t('seo.notFoundTitle'), description: t('notFound.text'), noindex: true });
  return null;
};
